import type { ListIteratee } from 'lodash';
import _ from 'lodash';
import { DISPLAY_MODE } from '@/main/app.constants';
import type { PersistenceLevel } from '@/core/flux.service';
import { Store } from '@/core/flux.service';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { getDefaultMaxCapsuleDuration } from '@/services/systemConfiguration.utilities';
import type { FrontendDuration } from '@/services/systemConfiguration.types';

/**
 * A base store that adds standard functionality for all stores that represent a tool used to create calculated series
 * or conditions.
 */

export interface ToolConditionDuration {
  value?: number | null;
  uom?: string | null;
}

export interface ToolConditionMetadata {
  maximumDuration?: ToolConditionDuration;
}

export interface ToolSignalMetadata {
  valueUnitOfMeasure?: string;
  samplingInterval?: number | null;
  units?: string;
}

export interface ToolAssetReference {
  id?: string;
  name?: string;
  color?: string | null;
}

export interface ToolItem {
  id?: string;
  name?: string;
  redacted?: boolean;
  itemType?: string;
  valueUnitOfMeasure?: string;
  signalMetadata?: ToolSignalMetadata;
  conditionMetadata?: ToolConditionMetadata;
  assets?: ToolAssetReference[] | null;
}

export interface ToolParameter {
  name?: string;
  item?: ToolItem;
  unbound?: boolean;
}

export interface ToolParameterDefinition {
  /** Predicate used to match parameters when rehydrating. */
  predicate: ListIteratee<ToolParameter> | string | string[];
  /** True when the parameter accepts multiple items. */
  multiple?: boolean;
}

export type ParameterDefinitions = Record<string, ToolParameterDefinition>;

export interface ToolSetNamePayload {
  type: string;
  name: string;
  id?: string;
}

export interface DurationWithUnits {
  value?: number | null;
  units?: string | null;
}

export interface BaseToolPersistedState {
  id?: string;
  name?: string;
  originalParameters: ToolItem[];
  maximumDuration?: DurationWithUnits | null;
  advancedParametersCollapsed: boolean;
  configVersion?: string | number | null;
}

export type ToolConfigValue =
  | string
  | number
  | boolean
  | null
  | DurationWithUnits
  | FrontendDuration
  | ToolItem
  | ToolItem[]
  | ToolConfigValue[]
  | { [key: string]: ToolConfigValue };

export interface ToolConfigParams {
  type: string;
  advancedParametersCollapsed: boolean;
  [key: string]: ToolConfigValue;
}

export interface ToolSetMaximumDurationPayload {
  type: string;
  value: number;
  units: string;
}

export interface ToolSetAdvancedParametersPayload {
  type: string;
  collapsed: boolean;
}

export interface ToolParameterItemPayload {
  type: string;
  name: string;
  item?: ToolItem | null;
}

export interface ToolDisplayModePayload {
  mode: string;
  type: string;
}

export interface ToolRehydratePayload {
  type: string;
  parameters?: ToolParameter[];
  formula?: string;
}

export abstract class BaseToolStore extends Store {
  abstract type: string;
  parameterDefinitions: ParameterDefinitions = {};
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  readonly TOOL_ITEM_PROPS = [
    'id',
    'name',
    'redacted',
    'itemType',
    'valueUnitOfMeasure',
    'signalMetadata',
    'conditionMetadata',
    'assets',
  ];

  /**
   * Used to rehydrate the tool in order to edit the calculated series.
   *
   * @param {Object} payload - An object with the necessary state to populate the edit form.
   * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
   * @param {Object[]} payload.parameters - The parameters used in the formula
   * @param {String} payload.formula - The formula for the calculated item
   */
  baseToolRehydrateForEdit = (payload: ToolRehydratePayload) => {
    if (payload.type === this.type) {
      const parameters = _.chain(payload.parameters ?? [])
        .reject('unbound')
        .map((parameter) => _.pick(parameter, ['item', 'name']))
        .value();
      this.initialize();
      // The UIConfig is intentionally merged first because in pre-R18 versions some of the parameters were
      // stored as ids in the stores. For example `signalToAggregate` in histograms may be in UIConfig as an ID,
      // but we can safely overwrite that with the item from the parameters. CRAB-11230
      const config = this.extractConfigFromPayload(payload);
      this.state.merge(this.migrateSavedConfig(config, parameters, payload.formula));
      this.state.set('originalParameters', _.map(parameters, 'item'));
      _.forEach(this.parameterDefinitions, (definition, name) => {
        const iteratee = definition.predicate as ListIteratee<ToolParameter>;
        const selected = _.chain(parameters)
          .filter(iteratee)
          .map((parameter) => _.pick(parameter.item, this.TOOL_ITEM_PROPS) as ToolItem)
          .thru((items) => (definition.multiple ? items : _.head(items)))
          .value();
        this.state.set(name, selected);
      });
    }
  };

  /**
   * Resets all parameters if switching to new instance of the form. Also clears state when closing to ensure extra
   * state doesn't hang around and populate worksteps.
   *
   * @param {Object} payload - Object containing state information
   * @param {String} payload.mode - The display mode being set, one of DISPLAY_MODE
   * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
   */
  protected reset = (payload: ToolDisplayModePayload) => {
    if (payload.mode === DISPLAY_MODE.NEW && (payload.type === this.type || payload.type === TREND_TOOLS.OVERVIEW)) {
      this.initialize();
    }
  };

  private extractConfigFromPayload(payload: ToolRehydratePayload): ToolConfigParams {
    const rawConfig = _.omit(payload, [
      'type',
      'parameters',
      'formula',
      'signalMetadata',
      'conditionMetadata',
    ]) as Partial<ToolConfigParams>;
    const advancedCollapsed = rawConfig.advancedParametersCollapsed;

    return {
      advancedParametersCollapsed:
        typeof advancedCollapsed === 'boolean'
          ? advancedCollapsed
          : Boolean(this.state.get('advancedParametersCollapsed')),
      ..._.omit(rawConfig, ['advancedParametersCollapsed']),
    } as ToolConfigParams;
  }

  protected getBasePersistedState(): BaseToolPersistedState {
    const originalParameters = this.state.get('originalParameters') as ToolItem[] | undefined;
    const maximumDuration = this.state.get('maximumDuration') as DurationWithUnits | null | undefined;
    const advancedParametersCollapsed = this.state.get('advancedParametersCollapsed') as boolean | undefined;
    const configVersion = this.state.get('configVersion') as string | number | null | undefined;

    return {
      id: this.state.get('id') as string | undefined,
      name: this.state.get('name') as string | undefined,
      originalParameters: originalParameters ?? [],
      maximumDuration: maximumDuration ?? undefined,
      advancedParametersCollapsed: advancedParametersCollapsed ?? true,
      configVersion: configVersion ?? undefined,
    };
  }

  protected applyBasePersistedState(state: Partial<BaseToolPersistedState>) {
    if ('id' in state) {
      this.state.set('id', state.id);
    }
    if ('name' in state) {
      this.state.set('name', state.name);
    }
    if ('originalParameters' in state && state.originalParameters !== undefined) {
      this.state.set('originalParameters', state.originalParameters);
    }
    if ('maximumDuration' in state) {
      this.state.set('maximumDuration', state.maximumDuration ?? undefined);
    }
    if ('advancedParametersCollapsed' in state && state.advancedParametersCollapsed !== undefined) {
      this.state.set('advancedParametersCollapsed', state.advancedParametersCollapsed);
    }
    if ('configVersion' in state) {
      this.state.set('configVersion', state.configVersion);
    }
  }

  protected readonly handlers = {
    /**
     * Sets the name.
     *
     * @param {Object} payload - an Object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {String} payload.name - The name of the calculated series
     * @param {String} payload.id - The id of the calculated series
     */
    TOOL_SET_SEARCH_NAME: (payload: ToolSetNamePayload) => {
      if (payload.type === this.type && (!payload.id || payload.id === this.state.get('id'))) {
        this.state.set('name', payload.name);
        this.state.commit();
      }
    },

    /**
     * Sets the maximum length/duration of a capsule for tools like Value Search and Composite Search.
     *
     * @param {Object} payload - an Object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {Number} payload.value - The number that indicates how long the duration is
     * @param {String} payload.units - The units that the value represents
     */
    TOOL_SET_MAXIMUM_DURATION: (payload: ToolSetMaximumDurationPayload) => {
      if (payload.type === this.type) {
        this.state.set('maximumDuration', {
          units: payload.units,
          value: payload.value,
        });
      }
    },

    /**
     * Sets the state of the expandable advanced parameters section.
     *
     * @param {Object} payload - an Object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {String} payload.collapsed - True if the advanced section is collapsed.
     */
    TOOL_SET_ADVANCED_PARAMETERS_COLLAPSED_STATE: (payload: ToolSetAdvancedParametersPayload) => {
      if (payload.type === this.type) {
        this.state.set('advancedParametersCollapsed', payload.collapsed);
      }
    },

    /**
     * Sets or adds, if multiple, one of the input parameters.
     *
     * @param {Object} payload - an object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {String} payload.name - The name of the parameter. Must be defined as one of the parameterDefinitions
     * @param {Object} payload.item - The item to set
     */
    TOOL_SET_PARAMETER_ITEM: (payload: ToolParameterItemPayload) => {
      if (payload.type === this.type) {
        const definition = _.get(this.parameterDefinitions, payload.name);
        if (!definition) {
          throw new TypeError(`${payload.name} is not a valid parameter item`);
        }

        if (definition.multiple && !Array.isArray(this.state.get(payload.name))) {
          this.state.set(payload.name, []);
        }

        // For multi-entry mode, don't add an item if it is already in the list.
        if (
          definition.multiple &&
          Array.isArray(this.state.get(payload.name)) &&
          payload.item &&
          _.includes(
            (this.state.get(payload.name) as ToolItem[]).map((i) => i.id),
            payload.item.id,
          )
        ) {
          return;
        }

        const item = _.isNil(payload.item) ? payload.item : (_.pick(payload.item, this.TOOL_ITEM_PROPS) as ToolItem);
        if (definition.multiple) {
          this.state.push(payload.name, item);
        } else {
          this.state.set(payload.name, item);
        }
      }
    },

    /**
     * Unsets or removes, if multiple, one of the input parameters.
     *
     * @param {Object} payload - an object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {String} payload.name - The name of the parameter. Must be defined as one of the parameterDefinitions
     * @param {Object} payload.item - The item to remove
     */
    TOOL_UNSET_PARAMETER_ITEM: (payload: ToolParameterItemPayload) => {
      if (payload.type === this.type) {
        const definition = _.get(this.parameterDefinitions, payload.name);
        if (!definition) {
          throw new TypeError(`${payload.name} is not a valid parameter item`);
        }

        if (definition.multiple) {
          const existing = this.state.get(payload.name) as ToolItem[] | undefined;
          const index = _.findIndex(existing, ['id', payload.item?.id]);
          if (index > -1 && existing) {
            this.state.splice(payload.name, [index, 1]);
          }
        } else {
          this.state.unset(payload.name);
        }
      }
    },

    TOOL_REHYDRATE_FOR_EDIT: this.baseToolRehydrateForEdit,

    INVESTIGATE_SET_DISPLAY_MODE: this.reset,
  };

  get baseHandlers() {
    return this.handlers;
  }

  get id() {
    return this.state.get('id');
  }

  get name() {
    return this.state.get('name');
  }

  get originalParameters() {
    return this.state.get('originalParameters');
  }

  get signalMetadata() {
    return this.state.get('signalMetadata');
  }

  get conditionMetadata() {
    return this.state.get('conditionMetadata');
  }

  get advancedParametersCollapsed() {
    return this.state.get('advancedParametersCollapsed');
  }

  get configParams(): ToolConfigParams {
    const serialized = this.state.serialize() as Record<string, ToolConfigValue>;
    const keysToOmit = ['id', 'name', 'originalParameters'].concat(Object.keys(this.parameterDefinitions));
    const remaining = _.omit(serialized, keysToOmit) as Record<string, ToolConfigValue>;
    const advanced = this.state.get('advancedParametersCollapsed');
    const config: ToolConfigParams = {
      type: this.type,
      advancedParametersCollapsed: typeof advanced === 'boolean' ? advanced : true,
      ...remaining,
    };

    return this.modifyConfigParams(config);
  }

  get maximumDuration() {
    return _.isUndefined(this.state.get('maximumDuration'))
      ? getDefaultMaxCapsuleDuration()
      : this.state.get('maximumDuration');
  }

  /**
   * Provides a hook that can be overridden by the derived store. Previous versions of the state will be passed
   * into the migrateSavedConfig function. By default it returns the state unchanged.
   *
   * @param config - The UIConfig state to populate the form
   * @param parameters - The parameters used in the formula
   * @param formula - The formula for the calculated item
   */
  migrateSavedConfig<TConfig extends ToolConfigParams>(
    config: TConfig,
    _parameters: ToolParameter[],
    _formula?: string,
  ): TConfig {
    return config;
  }

  /**
   * Used to rehydrate the tool in order to edit the calculated series.
   *
   * @param {Object} payload - An object with the necessary state to populate the edit form.
   * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
   * @param {Object[]} payload.parameters - The parameters used in the formula
   * @param {String} payload.formula - The formula for the calculated item
   */
  rehydrateForEdit(payload: ToolRehydratePayload) {
    if (payload.type === this.type) {
      const parameters = _.chain(payload.parameters ?? [])
        .reject('unbound')
        .map((parameter) => _.pick(parameter, ['item', 'name']))
        .value();
      this.initialize();
      // The UIConfig is intentionally merged first because in pre-R18 versions some of the parameters were
      // stored as ids in the stores. For example `signalToAggregate` in histograms may be in UIConfig as an ID,
      // but we can safely overwrite that with the item from the parameters. CRAB-11230
      const config = this.extractConfigFromPayload(payload);
      this.state.merge(this.migrateSavedConfig(config, parameters, payload.formula));
      this.state.set('originalParameters', _.map(parameters, 'item'));
      _.forEach(this.parameterDefinitions, (definition, name) => {
        const iteratee = definition.predicate as ListIteratee<ToolParameter>;
        const selected = _.chain(parameters)
          .filter(iteratee)
          .map((parameter) => _.pick(parameter.item, this.TOOL_ITEM_PROPS) as ToolItem)
          .thru((items) => (definition.multiple ? items : _.head(items)))
          .value();
        this.state.set(name, selected);
      });
    }
  }

  /**
   * Provides a hook that can be overridden by the derived store. The configParams will be passed which is stored
   * in the UIConfig property and which can be modified by this function.
   *
   * @param {Object} config - The state that will be saved to UIConfig
   * @return {Object} The modified config
   */
  modifyConfigParams<TConfig extends ToolConfigParams>(config: TConfig): TConfig {
    return config;
  }

  /**
   * Small set of utility functions for building up formulas.
   */
  formulaBuilder = {
    /**
     * Create a variable reference.
     *
     * @param {String} name - The variable name
     * @param {String} [suffix] - Appended to the name if present
     * @returns {String} The variable name, e.g. $input
     */
    var(varName: string, suffix?: string): string {
      return `$${varName}${suffix || ''}`;
    },

    /**
     * Invoke an operator with arguments.
     *
     * @param operator - The name of the operator
     * @param args - The arguments to pass to the operator
     * returns The invocation of an operator, e.g. union($a, $b)
     */
    operator(operator: string, args: string[]): string {
      return `${operator}(${args.join(', ')})`;
    },

    /**
     * Formats a duration object as a string.
     *
     * @param {Object} duration - The duration
     * @param {String|Number} duration.value - The number that indicates how long the duration is
     * @param {String} duration.units - The units that the value represent
     * returns The formatted duration, e.g. 5min
     */
    duration(duration: { value: string | number; units: string }): string {
      return `${duration.value}${duration.units}`;
    },
  };
}
